import { PinnedTabScope } from "@signalist/shared-types";

import { CampaignPinnedTabFilters } from "@/pages/Private/pages/Campaigns/utils/campaignPinnedFilterTabs";
import { PinnedTabFilters } from "@/pages/Private/pages/List/utils/pinnedFilterTabs";
import { api } from "@/redux/api";

import {
	CampaignsOverviewPinnedTabDto,
	CampaignsOverviewPinnedTabSchema,
	CampaignsOverviewPinnedTabsResponse,
	CampaignsOverviewPinnedTabsResponseSchema,
	ListOverviewPinnedTabDeleteResponse,
	ListOverviewPinnedTabDeleteResponseSchema,
	ListOverviewPinnedTabDto,
	ListOverviewPinnedTabSchema,
	ListOverviewPinnedTabsResponse,
	ListOverviewPinnedTabsResponseSchema,
} from "./listOverviewPinnedTab.schema";

const ENDPOINT = "list-overview-pinned-tabs";

/**
 * Per-user pinned overview tabs, shared by the Lead List and Campaigns overviews (PREC-3880).
 *
 * Both surfaces hit the same endpoints, told apart by `scope`. Every read MUST send its scope:
 * the two overviews store different `filters` shapes in one column, so an unscoped response would
 * fail the receiving overview's Zod validation rather than merely showing the wrong chips. The
 * cache tag is scoped for the same reason — pinning on Campaigns must not restamp the List cache.
 */
export const listOverviewPinnedTabApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getPinnedTabs: builder.query<ListOverviewPinnedTabsResponse, void>({
			query: () => ({
				url: ENDPOINT,
				params: { scope: PinnedTabScope.LIST },
				responseSchema: ListOverviewPinnedTabsResponseSchema,
			}),
			providesTags: [{ type: "ListOverviewPinnedTab", id: PinnedTabScope.LIST }],
		}),
		getCampaignPinnedTabs: builder.query<CampaignsOverviewPinnedTabsResponse, void>({
			query: () => ({
				url: ENDPOINT,
				params: { scope: PinnedTabScope.CAMPAIGN },
				responseSchema: CampaignsOverviewPinnedTabsResponseSchema,
			}),
			providesTags: [{ type: "ListOverviewPinnedTab", id: PinnedTabScope.CAMPAIGN }],
		}),
		createPinnedTab: builder.mutation<
			ListOverviewPinnedTabDto,
			{ name: string; filters: PinnedTabFilters }
		>({
			query: (body) => ({
				url: ENDPOINT,
				method: "POST",
				body: { ...body, scope: PinnedTabScope.LIST },
				responseSchema: ListOverviewPinnedTabSchema,
			}),
			invalidatesTags: [{ type: "ListOverviewPinnedTab", id: PinnedTabScope.LIST }],
		}),
		createCampaignPinnedTab: builder.mutation<
			CampaignsOverviewPinnedTabDto,
			{ name: string; filters: CampaignPinnedTabFilters }
		>({
			query: (body) => ({
				url: ENDPOINT,
				method: "POST",
				body: { ...body, scope: PinnedTabScope.CAMPAIGN },
				responseSchema: CampaignsOverviewPinnedTabSchema,
			}),
			invalidatesTags: [{ type: "ListOverviewPinnedTab", id: PinnedTabScope.CAMPAIGN }],
		}),
		updatePinnedTab: builder.mutation<
			ListOverviewPinnedTabDto,
			{ id: number; name?: string; filters?: PinnedTabFilters }
		>({
			query: ({ id, ...body }) => ({
				url: `${ENDPOINT}/${id}`,
				method: "PATCH",
				body,
				responseSchema: ListOverviewPinnedTabSchema,
			}),
			invalidatesTags: [{ type: "ListOverviewPinnedTab", id: PinnedTabScope.LIST }],
		}),
		updateCampaignPinnedTab: builder.mutation<
			CampaignsOverviewPinnedTabDto,
			{ id: number; name?: string; filters?: CampaignPinnedTabFilters }
		>({
			query: ({ id, ...body }) => ({
				url: `${ENDPOINT}/${id}`,
				method: "PATCH",
				body,
				responseSchema: CampaignsOverviewPinnedTabSchema,
			}),
			invalidatesTags: [{ type: "ListOverviewPinnedTab", id: PinnedTabScope.CAMPAIGN }],
		}),
		deletePinnedTab: builder.mutation<ListOverviewPinnedTabDeleteResponse, number>({
			query: (id) => ({
				url: `${ENDPOINT}/${id}`,
				method: "DELETE",
				responseSchema: ListOverviewPinnedTabDeleteResponseSchema,
			}),
			invalidatesTags: [{ type: "ListOverviewPinnedTab", id: PinnedTabScope.LIST }],
		}),
		deleteCampaignPinnedTab: builder.mutation<ListOverviewPinnedTabDeleteResponse, number>({
			query: (id) => ({
				url: `${ENDPOINT}/${id}`,
				method: "DELETE",
				responseSchema: ListOverviewPinnedTabDeleteResponseSchema,
			}),
			invalidatesTags: [{ type: "ListOverviewPinnedTab", id: PinnedTabScope.CAMPAIGN }],
		}),
	}),
});

export const {
	useGetPinnedTabsQuery,
	useGetCampaignPinnedTabsQuery,
	useCreatePinnedTabMutation,
	useCreateCampaignPinnedTabMutation,
	useUpdatePinnedTabMutation,
	useUpdateCampaignPinnedTabMutation,
	useDeletePinnedTabMutation,
	useDeleteCampaignPinnedTabMutation,
} = listOverviewPinnedTabApi;
